import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const DEMO_API = `${process.env.REACT_APP_BACKEND_URL}/api/demo`;

// All demo localStorage keys use demo_ prefix to stay isolated from live site
const lsk = (key) => `demo_${key}`;

async function trackDemoVisit(nonce) {
  if (!nonce) return;
  try {
    let visitorId = localStorage.getItem(lsk("visitor_id"));
    if (!visitorId) {
      const bytes = new Uint8Array(32);
      window.crypto.getRandomValues(bytes);
      visitorId = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      localStorage.setItem(lsk("visitor_id"), visitorId);
    }
    const today = new Date().toISOString().split("T")[0];
    const hash = async (s) => {
      const buf = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
      return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
    };
    const total_id = await hash(visitorId + nonce);
    const today_id = await hash(visitorId + nonce + today);
    await axios.post(`${DEMO_API}/visit`, { total_id, today_id });
  } catch {
    // Non-critical
  }
}

// Clear stale per-post demo state when a reset is detected
function clearStaleState(newToken) {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key.startsWith(lsk("helped_")) ||
      key.startsWith(lsk("reported_")) ||
      key.startsWith(lsk("delete_token_"))
    )) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((k) => localStorage.removeItem(k));
  localStorage.setItem(lsk("reset_token"), newToken);
}

const ANIMAL_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_would-help/artifacts/7fc16y49_ChatGPT%20Image%20Sep%2021%2C%202026%2C%2006_24_24%20PM.png";

const S = {
  page: {
    backgroundColor: "#FFF4B8",
    minHeight: "100vh",
    fontFamily: "Arial, sans-serif",
    color: "#111",
    fontSize: "15px",
    lineHeight: "1.5",
  },
  wrap: {
    maxWidth: "820px",
    margin: "0 auto",
    padding: "28px 20px 60px",
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "6px",
  },
  h1: {
    fontFamily: "Arial, sans-serif",
    fontWeight: "bold",
    fontSize: "26px",
    lineHeight: "1.3",
    margin: 0,
    color: "#000",
    flex: 1,
  },
  animalImg: {
    width: "90px",
    height: "auto",
    flexShrink: 0,
    marginTop: "2px",
    opacity: 0.92,
  },
  hr: { border: "none", borderTop: "1px solid #bbb", margin: "18px 0" },
  hrIdea: { border: "none", borderTop: "1px solid #ccc", margin: "16px 0" },
  label: {
    display: "block",
    fontFamily: "Arial, sans-serif",
    fontWeight: "bold",
    fontSize: "15px",
    marginBottom: "6px",
  },
  textarea: {
    display: "block",
    width: "100%",
    minHeight: "90px",
    border: "1px solid #999",
    borderRadius: "2px",
    padding: "8px",
    fontFamily: "Arial, sans-serif",
    fontSize: "15px",
    resize: "vertical",
    backgroundColor: "#fff",
    color: "#111",
    lineHeight: "1.5",
  },
  textareaOver: { borderColor: "#cc0000" },
  formBottom: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "6px",
  },
  counter: { fontFamily: "Arial, sans-serif", fontSize: "12px", color: "#777" },
  counterOver: { color: "#cc0000", fontWeight: "bold" },
  btn: {
    padding: "4px 14px",
    fontFamily: "Arial, sans-serif",
    fontSize: "14px",
    cursor: "pointer",
    backgroundColor: "#ececec",
    border: "2px outset #ccc",
    color: "#000",
    borderRadius: "2px",
  },
  btnDisabled: { opacity: 0.5, cursor: "default" },
  helpBtnHelped: { border: "2px inset #ccc", backgroundColor: "#d0d0d0" },
  statusMsg: { fontFamily: "Arial, sans-serif", fontSize: "13px", marginTop: "8px" },
  ideaText: {
    fontFamily: "Arial, sans-serif",
    fontSize: "16px",
    margin: "0 0 5px",
    lineHeight: "1.5",
    color: "#000",
  },
  helpLine: { fontFamily: "Arial, sans-serif", fontSize: "13px", color: "#555", margin: "0 0 7px" },
  helpBtn: {
    padding: "3px 10px",
    fontFamily: "Arial, sans-serif",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#ececec",
    border: "2px outset #ccc",
    color: "#000",
    borderRadius: "2px",
  },
  muted: { fontFamily: "Arial, sans-serif", color: "#666", fontSize: "14px" },
  typeTab: {
    padding: "4px 12px",
    fontFamily: "Arial, sans-serif",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#ececec",
    border: "2px outset #ccc",
    color: "#000",
    borderRadius: "2px",
  },
  typeTabActive: { border: "2px inset #ccc", backgroundColor: "#d0d0d0" },
};

const STATUS_MESSAGES = {
  success: "Your idea has been added.",
  rate_limit: "You've submitted too many ideas recently. Please try again later.",
  error: "Something went wrong. Please try again.",
};

/* ── Status message for demo submission forms ── */
function DemoStatusMsg({ status }) {
  if (!status) return null;
  if (status === "blocked") {
    return (
      <div data-testid="demo-moderation-message" style={{ marginTop: "8px" }}>
        <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#cc0000", margin: "0 0 4px" }}>
          This might contain personal information or content that isn&rsquo;t allowed here. Please edit and try again.
        </p>
        <p style={{ fontFamily: "Arial", fontSize: "13px", margin: 0 }}>
          <Link to="/rules" style={{ color: "blue", textDecoration: "underline" }}>See the rules</Link>
        </p>
      </div>
    );
  }
  return (
    <p style={{ fontFamily: "Arial", fontSize: "13px", marginTop: "8px", color: status === "success" ? "#333" : "#cc0000" }}>
      {STATUS_MESSAGES[status]}
    </p>
  );
}

function DemoIdeaEntry({ idea, sitePaused, onDelete }) {
  const storageKey = lsk(`helped_${idea.id}`);
  const reportKey  = lsk(`reported_${idea.id}`);
  const deleteKey  = lsk(`delete_token_${idea.id}`);
  const [helped, setHelped] = useState(() => localStorage.getItem(storageKey) === "1");
  const [isReported, setIsReported] = useState(() => localStorage.getItem(reportKey) === "1");
  const [canDelete] = useState(() => !!localStorage.getItem(deleteKey));
  const [count, setCount] = useState(idea.help_count);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (helped) {
        const res = await axios.delete(`${DEMO_API}/ideas/${idea.id}/help`);
        setCount(res.data.help_count);
        localStorage.removeItem(storageKey);
        setHelped(false);
      } else {
        const res = await axios.post(`${DEMO_API}/ideas/${idea.id}/help`);
        setCount(res.data.help_count);
        localStorage.setItem(storageKey, "1");
        setHelped(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const report = async () => {
    if (isReported) return;
    try {
      await axios.post(`${DEMO_API}/ideas/${idea.id}/report`);
      localStorage.setItem(reportKey, "1");
      setIsReported(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async () => {
    const token = localStorage.getItem(deleteKey);
    if (!token) return;
    if (!window.confirm("Delete this idea?")) return;
    try {
      await axios.delete(`${DEMO_API}/ideas/${idea.id}`, { data: { token } });
      localStorage.removeItem(deleteKey);
      onDelete(idea.id);
    } catch (e) {
      console.error(e);
    }
  };

  const dateStr = new Date(idea.created_at).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });
  const smallLinkStyle = {
    background: "none", border: "none", padding: 0,
    color: "blue", textDecoration: "underline",
    cursor: "pointer", fontFamily: "Arial", fontSize: "11px",
  };

  return (
    <div data-testid="demo-idea-card">
      <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#777", margin: "0 0 3px" }}>
        I would&hellip;
      </p>
      <p data-testid="demo-idea-text" style={S.ideaText}>{idea.text}</p>
      <p style={S.helpLine}>
        <span data-testid="demo-help-count">{count}</span>{" "}
        would help &nbsp;&middot;&nbsp;{" "}
        <span style={{ color: "#888" }}>{dateStr}</span>
      </p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
        <button
          data-testid="demo-help-button"
          onClick={toggle}
          disabled={busy || sitePaused}
          style={{
            ...S.helpBtn,
            ...(helped ? S.helpBtnHelped : {}),
            ...(busy ? S.btnDisabled : {}),
          }}
        >
          I would help
        </button>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {canDelete && (
            <button data-testid="demo-delete-button" onClick={handleDelete} style={smallLinkStyle}>
              Delete
            </button>
          )}
          {isReported ? (
            <span style={{ fontFamily: "Arial", fontSize: "11px", color: "#555" }}>Reported. Thanks.</span>
          ) : (
            <button data-testid="demo-report-button" onClick={report} style={smallLinkStyle}>
              Report
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Demo() {
  const [wouldText, setWouldText] = useState("");
  const [ideas, setIdeas] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [wouldSubmitting, setWouldSubmitting] = useState(false);
  const [wouldStatus, setWouldStatus] = useState(null);
  const [sitePaused, setSitePaused] = useState(false);

  const loadIdeas = useCallback(async (p, append = false) => {
    try {
      const res = await axios.get(`${DEMO_API}/ideas?page=${p}`);
      if (append) {
        setIdeas((prev) => [...prev, ...res.data.ideas]);
      } else {
        setIdeas(res.data.ideas);
      }
      setHasMore(res.data.has_more);
      setPage(p);
      if (res.data.paused !== undefined) setSitePaused(res.data.paused);
      if (p === 1) {
        // Handle reset token — clears stale per-post state if demo was reset
        const serverToken = res.data.demo_reset_token;
        if (serverToken) {
          const storedToken = localStorage.getItem(lsk("reset_token"));
          if (storedToken !== serverToken) {
            clearStaleState(serverToken);
          }
        }
        // Track demo participant
        if (res.data.participant_nonce) {
          trackDemoVisit(res.data.participant_nonce);
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadIdeas(1).finally(() => setInitialLoading(false));
  }, [loadIdeas]);

  const doSubmit = async (trimmed, postType, setText, setSubmitting, setStatus) => {
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await axios.post(`${DEMO_API}/ideas`, { text: trimmed, post_type: postType });
      if (res.data.delete_token) {
        localStorage.setItem(lsk(`delete_token_${res.data.id}`), res.data.delete_token);
      }
      const { delete_token: _dt, ...ideaData } = res.data;
      setIdeas((prev) => [ideaData, ...prev]);
      setText("");
      setStatus("success");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "CONTACT_INFO_BLOCKED" || detail === "AI_MODERATION_BLOCKED") setStatus("blocked");
      else if (err.response?.status === 429 || detail === "RATE_LIMITED") setStatus("rate_limit");
      else setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleWouldSubmit = (e) => {
    e.preventDefault();
    const trimmed = wouldText.trim();
    if (!trimmed || wouldSubmitting) return;
    doSubmit(trimmed, "would", setWouldText, setWouldSubmitting, setWouldStatus);
  };

  const handleShowMore = async () => {
    setLoadingMore(true);
    await loadIdeas(page + 1, true);
    setLoadingMore(false);
  };

  const wouldOver = wouldText.length > 280;

  if (!initialLoading && sitePaused) {
    return (
      <div
        data-testid="demo-paused-page"
        style={{
          backgroundColor: "#FFF4B8", minHeight: "100vh", fontFamily: "Arial, sans-serif",
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: "60px 24px", textAlign: "center",
        }}
      >
        <h1 style={{ fontFamily: "Arial", fontWeight: "bold", fontSize: "26px", color: "#111", margin: "0 0 10px" }}>
          Site paused for maintenance
        </h1>
        <p style={{ fontFamily: "Arial", fontSize: "16px", color: "#555", margin: "0 0 36px" }}>Check back soon!</p>
        <img src={ANIMAL_URL} alt="" aria-hidden="true" style={{ width: "200px", height: "auto", opacity: 0.95 }} />
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        <div data-testid="demo-site-header" style={S.headerRow}>
          <h1 style={S.h1}>What would you do if enough people would help?</h1>
          <img src={ANIMAL_URL} alt="" aria-hidden="true" style={S.animalImg} className="hidden sm:block" />
        </div>

        <p style={{ fontFamily: "Arial, sans-serif", fontSize: "14px", color: "#111", margin: "8px 0 0" }}>
          This is a thing for seeing what people might help each other do.
        </p>

        <hr style={S.hr} />

        {/* Demo notice */}
        <p data-testid="demo-notice" style={{ fontFamily: "Arial", fontSize: "13px", color: "#555", margin: "0 0 18px", fontStyle: "italic" }}>
          This is a demo. Nothing posted here appears on the main site.{" "}
          <Link to="/" style={{ color: "blue", textDecoration: "underline" }}>Go to the main site</Link>
        </p>

        <img
          src={ANIMAL_URL}
          alt=""
          aria-hidden="true"
          className="block sm:hidden"
          style={{ width: "120px", height: "auto", opacity: 0.92, marginBottom: "16px" }}
        />

        {/* WWYD submission form */}
        <form data-testid="demo-idea-input-form" onSubmit={handleWouldSubmit}>
          <label htmlFor="demo-would-input" style={S.label}>
            What would you do if enough people would help?
          </label>
          {sitePaused && (
            <p style={{ fontFamily: "Arial", fontSize: "13px", color: "#555", margin: "0 0 8px", fontStyle: "italic" }}>
              This site is temporarily paused. You can still browse existing ideas.
            </p>
          )}
          <textarea
            id="demo-would-input"
            data-testid="demo-idea-textarea"
            value={wouldText}
            onChange={(e) => { setWouldText(e.target.value); setWouldStatus(null); }}
            rows={4}
            placeholder="I would..."
            disabled={sitePaused}
            style={{
              ...S.textarea,
              ...(wouldOver ? S.textareaOver : {}),
              ...(sitePaused ? { backgroundColor: "#f5f5f5", color: "#aaa", cursor: "not-allowed" } : {}),
            }}
          />
          <div style={S.formBottom}>
            <button
              data-testid="demo-submit-button"
              type="submit"
              disabled={wouldSubmitting || !wouldText.trim() || wouldOver || sitePaused}
              style={{ ...S.btn, ...(wouldSubmitting || !wouldText.trim() || wouldOver ? S.btnDisabled : {}) }}
            >
              {wouldSubmitting ? "Submitting\u2026" : "Submit anonymously"}
            </button>
            <span data-testid="demo-char-counter" style={{ ...S.counter, ...(wouldOver ? S.counterOver : {}) }}>
              {wouldText.length}&nbsp;/&nbsp;280
            </span>
          </div>
          <DemoStatusMsg status={wouldStatus} />
        </form>

        <hr style={S.hr} />

        {/* Ideas stream */}
        {initialLoading ? (
          <p style={S.muted}>Loading&hellip;</p>
        ) : (
          <div data-testid="demo-ideas-stream">
            {ideas.length === 0 ? (
              <p style={S.muted}>No ideas yet.</p>
            ) : (
              ideas.map((idea) => (
                <div key={idea.id}>
                  <DemoIdeaEntry
                    idea={idea}
                    sitePaused={sitePaused}
                    onDelete={(id) => setIdeas((prev) => prev.filter((i) => i.id !== id))}
                  />
                  <hr style={S.hrIdea} />
                </div>
              ))
            )}
            {hasMore && (
              <button
                data-testid="demo-show-more-button"
                onClick={handleShowMore}
                disabled={loadingMore}
                style={{ ...S.btn, ...(loadingMore ? S.btnDisabled : {}) }}
              >
                {loadingMore ? "Loading\u2026" : "Show more"}
              </button>
            )}
          </div>
        )}

        {/* Footer */}
        {!initialLoading && (
          <div style={{ marginTop: "48px", paddingTop: "10px", borderTop: "1px solid #ccc" }}>
            <p style={{ fontFamily: "Arial", fontSize: "11px", color: "#555", margin: "0 0 6px" }}>
              Ideas are submitted anonymously by users. Appearing here does not mean an idea has been verified or endorsed by this site.
            </p>
            <p style={{ fontFamily: "Arial", fontSize: "12px", color: "#555", margin: 0 }}>
              <Link to="/rules" style={{ color: "blue", textDecoration: "underline" }}>Rules</Link>
              {" \u00b7 "}
              <Link to="/privacy" style={{ color: "blue", textDecoration: "underline" }}>Privacy</Link>
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
